/**
 * Session utilisateur côté client.
 *
 * `GET /api/auth/session` est la première requête de l'application : elle dit d'un coup
 * si l'utilisateur est connecté, si le serveur tourne en bypass de développement (le
 * bandeau d'avertissement en dépend), et — depuis le lot 2 de la spec CIAM — si le jeton
 * MSAL porte déjà le rôle `exiplan.utilisateur` (`appRole`, § 3.5.3).
 *
 * Amorçage : quand la configuration CIAM est posée (`ciamConfigured`), `initAuth()` termine
 * un éventuel retour de `loginRedirect()` avant le premier appel à `/auth/session` — c'est
 * lui qui pose le jeton que `lib/api.js` attache. Sans configuration CIAM (bypass de
 * développement, ou coexistence avec le cookie hérité), l'appel part sans jeton.
 *
 * **Ne s'exécute qu'une seule fois** (revue qualité du lot 2) : l'effet d'amorçage a un
 * tableau de dépendances vide à dessein. `useNavigate()` change de référence à chaque
 * changement de route (react-router 7.18) ; le lister en dépendance rejouerait l'amorçage —
 * et donc `navigate(returnTo)` — à chaque navigation, écrasant au passage la langue déjà
 * choisie par un second `GET /auth/session` non désiré. `initAuth()` est lui-même défensif
 * (§ `lib/auth.js`) : le `returnTo`/l'erreur qu'il rend ne sont de toute façon consommés
 * qu'une fois, mais l'effet ne doit même pas retenter l'opération.
 *
 * `appRole` (tableau du § 3.5.3) :
 *   - `null` (bypass, ou mode cookie hérité) → rien de plus, `authenticated`/`anonymous` suffit
 *   - `{ inToken: true }` → cas nominal
 *   - `{ inToken: false, granted: true }` → le rôle vient d'être obtenu (ou le jeton est
 *     antérieur à son attribution) : un renouvellement forcé puis un second appel. Encore sans
 *     le rôle : si `grantedRecently` (attribué depuis moins de 5 min, calcul serveur), c'est la
 *     propagation annuaire → `activating` en phase `propagating` avec re-sondage automatique,
 *     JAMAIS `suspended` ; sinon un second essai après un court délai (§ 10 de la spec), puis
 *     `suspended` (coupure depuis le tenant, § 3.5.6)
 *   - `{ granted: false }` → `activating` (principal pas encore répliqué, Graph en échec, ou
 *     attribution désactivée hors prod)
 *
 * Re-sondage automatique (spec onboarding fluide, D-B6) : tant que le statut est `activating`,
 * `/auth/session` est rejoué seul à 3 s, 5 s puis toutes les 10 s, dans un budget de 120 s ;
 * `retry()` réarme ce budget. `activation` ({ phase, polling, nextInMs }) alimente l'indication
 * discrète d'`ActivationPage`. Une erreur réseau pendant un sondage ne passe pas en `offline` :
 * on est déjà sur un écran d'attente, le sondage suivant est simplement programmé. Le sondage
 * est annulé par `logout()`, par toute sortie de l'état d'attente et au démontage.
 *
 * Le profil est la source de vérité de la langue (chantier préférences, 2026-09-15) :
 * `user.locale` (écrit par `ProfilePage`) s'applique à l'ouverture de session.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, onUnauthenticated } from '../lib/api.js';
import {
  ciamConfigured,
  getAccessToken,
  initAuth,
  logout as msalLogout,
  msalInstance,
  onExternalLogout,
} from '../lib/auth.js';
import i18n from '../i18n/config.js';

const SessionContext = createContext(null);

/** Délai avant le second essai de propagation du rôle (§ 10 de la spec — « à trancher à
 *  l'écran, pas en avance » : posé ici, seul endroit d'où le retoucher). Ne sert plus que
 *  quand le serveur ne signale pas une attribution récente (`grantedRecently: false`). */
const ROLE_PROPAGATION_RETRY_DELAY_MS = 3000;

/** Délais de re-sondage de l'écran d'activation ; le dernier se répète. */
const ACTIVATION_POLL_DELAYS_MS = [3000, 5000, 10000];
/** Temps réel écoulé depuis le début du sondage au-delà duquel il s'arrête (« Réessayer » le réarme). */
const ACTIVATION_POLL_BUDGET_MS = 120_000;
/** Pas du compte à rebours exposé à l'écran. */
const ACTIVATION_TICK_MS = 1000;

const IDLE_ACTIVATION = { phase: null, polling: false, nextInMs: null };

const estUne401 = (error) => error?.status === 401 || error?.isUnauthenticated === true;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Retour à l'anonyme : le mode d'organisation du serveur ne dépend pas de la personne connectée. */
const versAnonyme = (previous) => ({
  ...ANONYMOUS_STATE,
  organisationStudio: previous.organisationStudio,
});

const ANONYMOUS_STATE = {
  status: 'anonymous',
  user: null,
  bypass: false,
  organisationStudio: 'local',
  providers: [],
};

export function SessionProvider({ children }) {
  const [state, setState] = useState({
    status: 'loading',
    user: null,
    bypass: false,
    organisationStudio: 'local',
    providers: [],
  });
  const [authError, setAuthError] = useState(null);
  const [activation, setActivation] = useState(IDLE_ACTIVATION);
  const navigate = useNavigate();

  // Machine de re-sondage : tout en refs, pour que les minuteurs ne dépendent d'aucun rendu.
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const phaseRef = useRef('granting');
  const pollCountRef = useRef(0);
  const generationRef = useRef(0);
  const evaluateRef = useRef(null);

  const fetchSession = useCallback(() => api.get('/auth/session').then(({ data }) => data), []);

  const applySession = useCallback((data) => {
    if (data.authenticated && data.user?.locale) {
      i18n.changeLanguage(data.user.locale);
    }
    setState({
      status: data.authenticated ? 'authenticated' : 'anonymous',
      user: data.user,
      bypass: data.bypass,
      organisationStudio: data.organisationStudio ?? 'local',
      providers: data.providers,
    });
  }, []);

  /** Arrête le sondage (sortie de l'état d'attente, retry, logout) et réarme son budget. */
  const stopActivation = useCallback(() => {
    generationRef.current += 1;
    clearTimeout(timerRef.current);
    startedAtRef.current = null;
    pollCountRef.current = 0;
    setActivation(IDLE_ACTIVATION);
  }, []);

  /** Programme le prochain sondage (avec compte à rebours), ou s'arrête une fois le budget épuisé. */
  const schedulePoll = useCallback(
    (phase) => {
      clearTimeout(timerRef.current);
      phaseRef.current = phase;
      if (startedAtRef.current == null) startedAtRef.current = Date.now();
      if (Date.now() - startedAtRef.current >= ACTIVATION_POLL_BUDGET_MS) {
        setActivation({ phase, polling: false, nextInMs: null });
        return;
      }
      const wait =
        ACTIVATION_POLL_DELAYS_MS[
          Math.min(pollCountRef.current, ACTIVATION_POLL_DELAYS_MS.length - 1)
        ];
      pollCountRef.current += 1;
      const generation = generationRef.current;
      let remaining = wait;

      const runPoll = async () => {
        setActivation({ phase, polling: true, nextInMs: null });
        try {
          const data = await fetchSession();
          if (generation !== generationRef.current) return;
          await evaluateRef.current(data, generation);
        } catch (error) {
          if (generation !== generationRef.current) return;
          // Session expirée : l'écran de connexion prend le relais, on ne sonde pas derrière.
          if (estUne401(error)) {
            stopActivation();
            return;
          }
          schedulePoll(phase);
        }
      };

      const tick = () => {
        if (generation !== generationRef.current) return;
        remaining -= ACTIVATION_TICK_MS;
        if (remaining > 0) {
          setActivation({ phase, polling: true, nextInMs: remaining });
          timerRef.current = setTimeout(tick, ACTIVATION_TICK_MS);
          return;
        }
        runPoll();
      };

      setActivation({ phase, polling: true, nextInMs: wait });
      timerRef.current = setTimeout(tick, ACTIVATION_TICK_MS);
    },
    [fetchSession, stopActivation],
  );

  /**
   * Interprète `appRole` (§ 3.5.3) au-dessus du statut authentifié/anonyme brut. `data.appRole`
   * n'est posé qu'en mode porteur — `null` en bypass ou pendant la coexistence cookie (D7).
   */
  const evaluateSession = useCallback(
    async (data, generation = generationRef.current) => {
      // Une évaluation périmée (« Réessayer », déconnexion, nouveau sondage) ne doit plus rien
      // écrire : vérifié après CHAQUE attente.
      const perimee = () => generation !== generationRef.current;
      const { appRole } = data;
      if (!appRole || appRole.inToken) {
        stopActivation();
        applySession(data);
        return;
      }
      const attendre = (phase, source) => {
        setState({
          status: 'activating',
          user: null,
          bypass: source.bypass,
          providers: source.providers,
        });
        schedulePoll(phase);
      };
      if (!appRole.granted) {
        attendre('granting', data);
        return;
      }
      // `granted: true, inToken: false` : le rôle vient d'être posé (ou le jeton en cache est
      // antérieur à son attribution) — un renouvellement forcé, puis un second passage. Une
      // erreur MSAL qui n'est PAS une simple absence de rôle (réseau, jeton irrécupérable)
      // ne doit jamais retomber sur l'écran « API injoignable » du mode développement : c'est
      // une anomalie d'authentification, traitée comme une déconnexion (§ 10 de la revue).
      try {
        await getAccessToken({ forceRefresh: true });
      } catch {
        if (perimee()) return;
        stopActivation();
        setState(versAnonyme);
        return;
      }
      if (perimee()) return;
      let second = await fetchSession();
      if (perimee()) return;
      if (!second.appRole?.inToken && second.appRole?.grantedRecently) {
        // Rôle attribué il y a peu : le jeton CIAM ne l'a pas encore pris en compte. On attend
        // sans rien conclure — jamais un faux « accès suspendu » pour un compte tout juste activé.
        attendre('propagating', second);
        return;
      }
      if (!second.appRole?.inToken) {
        // Délai de propagation annuaire (§ 10 de la spec) : un second essai avant de conclure
        // à une coupure — un faux « suspendu » à la première connexion serait un mauvais
        // message.
        await delay(ROLE_PROPAGATION_RETRY_DELAY_MS);
        if (perimee()) return;
        try {
          await getAccessToken({ forceRefresh: true });
        } catch {
          if (perimee()) return;
          stopActivation();
          setState(versAnonyme);
          return;
        }
        if (perimee()) return;
        second = await fetchSession();
        if (perimee()) return;
      }
      stopActivation();
      if (second.appRole?.inToken) {
        applySession(second);
      } else {
        setState({
          status: 'suspended',
          user: null,
          bypass: second.bypass,
          providers: second.providers,
        });
      }
    },
    [applySession, fetchSession, schedulePoll, stopActivation],
  );
  evaluateRef.current = evaluateSession;

  const refresh = useCallback(async () => {
    const generation = generationRef.current;
    try {
      const data = await fetchSession();
      if (generation !== generationRef.current) return;
      await evaluateSession(data, generation);
    } catch (error) {
      if (generation !== generationRef.current) return;
      // Un 401 est une vraie déconnexion, déjà posée en `anonymous` par `onUnauthenticated` :
      // la recouvrir de `offline` afficherait « API injoignable » au lieu de la connexion.
      if (estUne401(error)) return;
      // Une API injoignable n'est pas une déconnexion : on le distingue pour ne pas
      // afficher un écran de connexion trompeur quand le serveur est simplement arrêté.
      setState((previous) => ({ ...previous, status: 'offline' }));
    }
  }, [fetchSession, evaluateSession]);

  // Amorçage — une seule fois (§ commentaire d'en-tête). `navigate` et `refresh` sont capturés
  // à l'exécution de l'effet ; react-router garantit que la fonction reste utilisable même si
  // sa référence change ensuite.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (ciamConfigured) {
        const { returnTo, error } = await initAuth();
        if (cancelled) return;
        if (error) setAuthError('login_failed');
        if (returnTo) {
          try {
            navigate(returnTo, { replace: true });
          } catch {
            // Une navigation défaillante ne doit jamais empêcher l'amorçage de la session.
          }
        }
      }
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Démontage : plus aucun minuteur ni sondage en vol.
  useEffect(
    () => () => {
      generationRef.current += 1;
      clearTimeout(timerRef.current);
    },
    [],
  );

  useEffect(
    () => onUnauthenticated(() => setState((p) => ({ ...p, status: 'anonymous', user: null }))),
    [],
  );

  // Déconnexion multi-onglets (§ correctif) : un autre onglet a appelé `logout()` — celui-ci
  // efface le cache MSAL du compte qui vient de partir et repasse en anonyme sans attendre son
  // propre renouvellement de jeton pour s'en apercevoir.
  useEffect(
    () =>
      onExternalLogout(() => {
        msalInstance?.clearCache?.();
        stopActivation();
        setState(versAnonyme);
      }),
    [stopActivation],
  );

  const logout = useCallback(async () => {
    stopActivation();
    if (ciamConfigured) {
      // Coexistence (D7) : le cookie legacy peut encore exister sur ce navigateur (ancien
      // build, ou bascule récente) — on l'efface en best effort avant de partir vers MSAL,
      // pour ne jamais laisser une identité légère derrière soi (audit sécurité).
      await api.post('/auth/logout').catch(() => {});
      await msalLogout();
      return;
    }
    await api.post('/auth/logout');
    await refresh();
  }, [refresh, stopActivation]);

  /** Rejoue `/auth/session` — bouton « Réessayer » de l'écran d'activation (§ 3.5.3) ; réarme le
   *  budget du sondage automatique puis sonde tout de suite. */
  const retry = useCallback(async () => {
    const phase = phaseRef.current;
    stopActivation();
    // Pendant la requête (qui peut durer plusieurs secondes), « vérification en cours » — jamais
    // « vérifications arrêtées ».
    setActivation({ phase, polling: true, nextInMs: null });
    const generation = generationRef.current;
    await refresh();
    // Rien n'a repris la main (ex. API injoignable) : on ne laisse pas l'indication figée.
    if (generation === generationRef.current && pollCountRef.current === 0) {
      setActivation(IDLE_ACTIVATION);
    }
  }, [refresh, stopActivation]);

  return (
    <SessionContext.Provider value={{ ...state, refresh, logout, retry, authError, activation }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession doit être utilisé dans un SessionProvider.');
  return context;
}
